import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import axios from "axios";
import Header from "../common/Header";
import Footer from "../common/Footer";
import PageWrapper from "../common/PageWrapper";
import StepOneNeuf from './StepsNeuf/StepOneNeuf';
import StepOneOccasion from './StepsOccasion/StepOneOccasion';
import StepTwoNeuf from './StepsNeuf/StepTwoNeuf';
import StepTwoOccasion from './StepsOccasion/StepTwoOccasion';
import StepThreeNeuf from './StepsNeuf/StepThreeNeuf';
import StepThreeOccasion from './StepsOccasion/StepThreeOccasion';
import StepFourNeuf from './StepsNeuf/StepFourNeuf';
import StepFourOccasion from './StepsOccasion/StepFourOccasion';
import StepFiveCommon from './StepFiveCommon';
import "./VehiculeForm.css";
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const VehiculeForm = ({ menuState }) => {
    const { isMenuOpen, setIsMenuOpen, onMenuToggle } = menuState || {};
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002/api";

    const [currentStep, setCurrentStep] = useState(1);
    const [isNavigating, setIsNavigating] = useState(false);
    const [vehiculeType, setVehiculeType] = useState(null); // 'Neuf' or 'Occasion'
    const [vendeurs, setVendeurs] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const { vehiculeId: urlVehiculeId } = useParams();
    const [vehiculeId, setVehiculeId] = useState(null);
    const [submissionError, setSubmissionError] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        numero_dossier: '',
        statut: 'Commande',
        vendeur_id: null,
        concession_id: null,
        concession_groupe_id: null,
        etat: null, // VO VN
        nom_client: '',
        telephone_client: '',
        numero_chassis: '',

        stepOneData: {},
        stepTwoData: {},
        stepThreeData: {},
        stepFourData: {}
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem("token");

                // Récupération des informations de l'utilisateur connecté
                const userResponse = await axios.get(`${API_URL}/users/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (userResponse.data) {
                    setCurrentUser(userResponse.data);
                    const user = userResponse.data;

                    // Récupération des vendeurs en fonction du rôle de l'utilisateur
                    const vendeursEndpoint = user.statut === 'assistante'
                        ? `${API_URL}/assistantes/commercials`
                        : `${API_URL}/users/commercials`;

                    const vendeursResponse = await axios.get(vendeursEndpoint, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (vendeursResponse.data) {
                        let filteredVendeurs = vendeursResponse.data;

                        if (user.statut === 'assistante') {
                            filteredVendeurs = filteredVendeurs.filter(
                                vendeur => vendeur.groupe_id === user.groupe_id
                            );
                        }

                        setVendeurs(filteredVendeurs);
                    }
                }
            } catch (error) {
                console.error("Erreur lors de la récupération des données:", error);
            }
        };

        fetchData();
    }, [API_URL]);

    useEffect(() => {

        if (urlVehiculeId) {
            setVehiculeId(urlVehiculeId);
        }

        const fetchVehiculeData = async () => {
            if (!urlVehiculeId) return; // Vérifie si un ID est fourni

            setLoading(true); // Active l'état de chargement
            setError(null); // Réinitialise l'état d'erreur

            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(`${API_URL}/vehicules/${urlVehiculeId}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                if (response.data) {
                    const data = response.data;

                    // Organiser les données par étapes
                    const stepOneData = {};
                    const stepTwoData = {};
                    const stepThreeData = {};
                    const stepFourData = {};

                    // Règles pour organiser les données par étapes
                    // Étape 1: Données du véhicule (veh_*, occ_veh_*, occ_ra_*, ccs_*, ccv_*, vde_*)
                    Object.keys(data).forEach(key => {
                        if (key.startsWith('veh_') ||
                            key.startsWith('occ_veh_') ||
                            key.startsWith('occ_ra_') ||
                            key.startsWith('ccs_') ||
                            key.startsWith('ccv_') ||
                            key.startsWith('vde_')) {
                            // Convertir les valeurs numériques des boutons radio en chaînes de caractères
                            if (key === 'veh_33' || key === 'veh_34' || key === 'ccv_1' || key === 'veh_0' || key === 'veh_5' || key === 'occ_veh_32') {
                                stepOneData[key] = data[key] !== null && data[key] !== undefined ? data[key].toString() : null;
                            } else {
                                stepOneData[key] = data[key];
                            }
                        }
                    });

                    // Définir les valeurs par défaut pour les boutons radio de l'étape 1
                    const defaultStepOneRadioValues = {
                        veh_0: '0', // Type de véhicule (0: particulier, 1: professionnel)
                        veh_33: '0', // Type de carburant (0: essence, 1: diesel)
                        veh_34: '0', // Type de transmission (0: manuelle, 1: automatique)
                        ccv_1: '0', // Type de climatisation (0: manuelle, 1: automatique)
                        occ_veh_32: '0', // Présence du certificat d'immatriculation (0: non, 1: oui)
                        veh_5: '0', // Usage (0: non, 1: oui)
                    };

                    // Appliquer les valeurs par défaut pour les boutons radio de l'étape 1
                    Object.keys(defaultStepOneRadioValues).forEach(key => {
                        if (stepOneData[key] === undefined || stepOneData[key] === null) {
                            stepOneData[key] = defaultStepOneRadioValues[key];
                        }
                    });

                    // Détecter si les champs conditionnels sont remplis et définir les boutons radio en conséquence
                    // Pour occ_veh_32 (Présence du certificat d'immatriculation)
                    if (stepOneData.occ_veh_32_1 !== undefined && stepOneData.occ_veh_32_1 !== null && stepOneData.occ_veh_32_1 !== '') {
                        stepOneData.occ_veh_32 = '1'; // Si le champ de format est rempli, le certificat est présent
                    } else if (stepOneData.occ_veh_32_2 !== undefined && stepOneData.occ_veh_32_2 !== null && stepOneData.occ_veh_32_2 !== '') {
                        stepOneData.occ_veh_32 = '0'; // Si le champ de motif est rempli, le certificat n'est pas présent
                    }

                    // Pour veh_0 (Certificat de conformité Européen)
                    if (stepOneData.veh_4 !== undefined && stepOneData.veh_4 !== null && stepOneData.veh_4 !== '') {
                        stepOneData.veh_0 = '0'; // Si le champ de dénomination commerciale est rempli, pas de CoC
                    }

                    // Étape 2: Données des titulaires (tit_*, cotit_*, occ_tit_*, occ_cotit_*)
                    Object.keys(data).forEach(key => {
                        if (key.startsWith('tit_') ||
                            key.startsWith('cotit_') ||
                            key.startsWith('occ_tit_') ||
                            key.startsWith('occ_cotit_') ||
                            key.startsWith('vde_') ||
                            key.startsWith('occ_app_')) {
                            // Convertir les valeurs numériques des boutons radio en chaînes de caractères
                            if (key === 'tit_1' || key === 'tit_2' || key === 'cotit_0' || key === 'cotit_2' ||
                                key === 'occ_app_1' || key === 'occ_app_2' || key === 'occ_app_12' ||
                                key === 'occ_tit_1' || key === 'occ_tit_2' || key === 'occ_cotit_0' ||
                                key === 'occ_cotit_2') {
                                stepTwoData[key] = data[key] !== null && data[key] !== undefined ? data[key].toString() : null;
                            } else {
                                stepTwoData[key] = data[key];
                            }
                        }
                    });

                    // Définir les valeurs par défaut pour les boutons radio de l'étape 2
                    const defaultStepTwoRadioValues = {
                        // Ancien propriétaire
                        // occ_app_1: '0', // Type de vendeur (0: personne physique, 1: personne morale)
                        occ_app_2: '0', // Sexe du vendeur (0: homme, 1: femme)
                        occ_app_12: '0', // Type de cession (0: Céder, 1: Céder pour destruction)

                        // Nouveau propriétaire
                        // occ_tit_1: '0', // Type d'acquéreur (0: personne physique, 1: personne morale)
                        occ_tit_2: '0', // Sexe de l'acquéreur (0: homme, 1: femme)

                        // Co-titulaire
                        occ_cotit_0: '0', // Multi-propriété (0: non, 1: oui)
                        occ_cotit_2: '0', // Type de co-titulaire (0: personne physique, 1: personne morale)
                    };

                    // Appliquer les valeurs par défaut pour les boutons radio de l'étape 2
                    Object.keys(defaultStepTwoRadioValues).forEach(key => {
                        if (stepTwoData[key] === undefined || stepTwoData[key] === null) {
                            stepTwoData[key] = defaultStepTwoRadioValues[key];
                        }
                    });

                    // Détecter si les champs conditionnels sont remplis et définir les boutons radio en conséquence
                    // Pour occ_app_1 (Type de vendeur)
                    if (stepTwoData.occ_app_3 !== undefined && stepTwoData.occ_app_3 !== null && stepTwoData.occ_app_3 !== '') {
                        stepTwoData.occ_app_1 = '0'; // Si le nom et prénom sont remplis, c'est une personne physique
                    } else if (stepTwoData.occ_app_4 !== undefined && stepTwoData.occ_app_4 !== null && stepTwoData.occ_app_4 !== '') {
                        stepTwoData.occ_app_1 = '1'; // Si la raison sociale est remplie, c'est une personne morale
                    }

                    // Pour occ_tit_1 (Type d'acquéreur)
                    if (stepTwoData.occ_tit_3 !== undefined && stepTwoData.occ_tit_3 !== null && stepTwoData.occ_tit_3 !== '') {
                        stepTwoData.occ_tit_1 = '0'; // Si le nom et prénom sont remplis, c'est une personne physique
                    } else if (stepTwoData.occ_tit_9 !== undefined && stepTwoData.occ_tit_9 !== null && stepTwoData.occ_tit_9 !== '') {
                        stepTwoData.occ_tit_1 = '1'; // Si la raison sociale est remplie, c'est une personne morale
                    }

                    // Pour occ_cotit_0 (Multi-propriété)
                    if (stepTwoData.occ_cotit_3 !== undefined && stepTwoData.occ_cotit_3 !== null && stepTwoData.occ_cotit_3 !== '') {
                        stepTwoData.occ_cotit_0 = '1'; // Si le nom et prénom du co-titulaire sont remplis, c'est une multi-propriété
                    }

                    // Vérifier si les données de l'ancien propriétaire sont présentes
                    const hasAncienProprietaireData = Object.keys(stepTwoData).some(key =>
                        key.startsWith('occ_app_') &&
                        stepTwoData[key] !== null &&
                        stepTwoData[key] !== undefined &&
                        stepTwoData[key] !== ''
                    );

                    // Si des données de l'ancien propriétaire sont présentes mais que le type n'est pas défini
                    if (hasAncienProprietaireData && (stepTwoData.occ_app_1 === null || stepTwoData.occ_app_1 === undefined)) {
                        // Déterminer le type en fonction des champs remplis
                        if (stepTwoData.occ_app_3 !== undefined && stepTwoData.occ_app_3 !== null && stepTwoData.occ_app_3 !== '') {
                            stepTwoData.occ_app_1 = '0'; // Personne physique
                        } else if (stepTwoData.occ_app_4 !== undefined && stepTwoData.occ_app_4 !== null && stepTwoData.occ_app_4 !== '') {
                            stepTwoData.occ_app_1 = '1'; // Personne morale
                        } else {
                            stepTwoData.occ_app_1 = '0'; // Par défaut, personne physique
                        }
                    }

                    // Étape 3: Données de location (loc_*, occ_app_*)
                    Object.keys(data).forEach(key => {
                        if (key.startsWith('loc_')) {
                            // Convertir les valeurs numériques des boutons radio en chaînes de caractères
                            if (key === 'loc_0' || key === 'loc_1' || key === 'loc_5' ||
                                key === 'loc_17' || key === 'loc_18') {
                                stepThreeData[key] = data[key] !== null && data[key] !== undefined ? data[key].toString() : null;
                            } else {
                                stepThreeData[key] = data[key];
                            }
                        }
                    });
                    const defaultStepThreeRadioValues = {
                        loc_0: '0', // 0 = pas en location, 1 = en location
                        loc_1: '0', // 0 = courte durée, 1 = longue durée
                        loc_5: '0', // 0 = personne physique, 1 = personne morale
                    };

                    Object.keys(defaultStepThreeRadioValues).forEach(key => {
                        if (stepThreeData[key] === undefined || stepThreeData[key] === null) {
                            stepThreeData[key] = defaultStepThreeRadioValues[key];
                        }
                    });

                    // Si des champs de location sont remplis, on force loc_0 = '1'
                    const hasLocationData = [
                        'loc_1', 'loc_3', 'loc_4', 'loc_5',
                        'loc_6', 'loc_7', 'loc_8',
                        'loc_9', 'loc_10', 'loc_11', 'loc_12',
                        'loc_13', 'loc_14', 'loc_15', 'loc_16',
                    ].some(k => {
                        const v = stepThreeData[k];
                        return v !== null && v !== undefined && v !== '';
                    });

                    if (hasLocationData) {
                        stepThreeData.loc_0 = '1';
                    }

                    // Si on a renseigné le loueur (raison sociale / SIRET), on force "longue durée"
                    if (
                      (stepThreeData.loc_3 && stepThreeData.loc_3 !== '') ||
                      (stepThreeData.loc_4 && stepThreeData.loc_4 !== '')
                    ) {
                      stepThreeData.loc_1 = '1';
                    }

                    // Si loc_6 (nom/prénom locataire) est renseigné => personne physique
                    // Si loc_7 ou loc_8 (raison sociale / SIRET locataire) sont renseignés => personne morale
                    if (stepThreeData.loc_6 && stepThreeData.loc_6 !== '') {
                        stepThreeData.loc_5 = '0';
                    } else if (
                        (stepThreeData.loc_7 && stepThreeData.loc_7 !== '') ||
                        (stepThreeData.loc_8 && stepThreeData.loc_8 !== '')
                    ) {
                        stepThreeData.loc_5 = '1';
                    }

                    // Étape 4: Données de signature (sig_*)
                    Object.keys(data).forEach(key => {
                        if (key.startsWith('sig_')) {
                            stepFourData[key] = data[key];
                        }
                    });

                    // Ajouter le group_id de la concession s'il existe
                    if (data.concession?.group_id) {
                        data.concession_groupe_id = data.concession.group_id;
                    }

                    // Fusionne les données récupérées avec la structure par défaut de formData
                    setFormData((prevState) => ({
                        ...prevState,
                        numero_dossier: data.numero_dossier || '',
                        statut: data.statut || 'Commande',
                        vendeur_id: data.vendeur_id || null,
                        concession_id: data.concession_id || null,
                        concession_groupe_id: data.concession_groupe_id || null,
                        etat: data.etat || null, // VO ou VN
                        nom_client: data.nom_client || '',
                        telephone_client: data.telephone_client || '',
                        numero_chassis: data.numero_chassis || '',
                        stepOneData: stepOneData, // Données organisées pour l'étape 1
                        stepTwoData: stepTwoData, // Données organisées pour l'étape 2
                        stepThreeData: stepThreeData, // Données organisées pour l'étape 3
                        stepFourData: stepFourData, // Données organisées pour l'étape 4
                    }));

                    // console.log("Données organisées par étapes:", {
                    //     stepOneData,
                    //     stepTwoData,
                    //     stepThreeData,
                    //     stepFourData
                    // });

                    // Définit le type de véhicule (Neuf ou Occasion)
                    setVehiculeType(data.etat === 'VN' ? 'Neuf' : 'Occasion');
                }
            } catch (error) {
                console.error("Erreur lors de la récupération des données :", error);
                setError("Erreur lors de la récupération des données du véhicule. Veuillez réessayer.");
            } finally {
                setLoading(false); // Désactive l'état de chargement
            }
        };

        fetchVehiculeData();
    }, [urlVehiculeId, API_URL]);

    const handleInputChange = (e) => {
      const { name, value } = e.target;

      console.log(`[handleInputChange] Champ modifié : ${name} => ${value}`);

      if (name === 'vendeur_id') {
          const selectedVendeur = vendeurs.find(v => v.id === parseInt(value, 10));
          console.log("[handleInputChange] Vendeur sélectionné :", selectedVendeur);

          setFormData(prevState => ({
              ...prevState,
              vendeur_id: value,
              concession_id: selectedVendeur?.concessions_id || null
          }));

          if (!selectedVendeur?.concessions_id) {
              console.warn("[handleInputChange] ⚠️ Aucun concession_id trouvé pour ce vendeur !");
          } else {
              console.log(`[handleInputChange] concession_id défini à : ${selectedVendeur.concessions_id}`);
          }
      } else {
          setFormData(prevState => ({
              ...prevState,
              [name]: value
          }));
      }
    };

    const handleVehiculeTypeChange = (type) => {
        setVehiculeType(type);
        setFormData(prevState => ({
            ...prevState,
            etat: type === 'Neuf' ? 'VN' : 'VO'
        }));
    };

    const handleStepData = (step, data) => {
        const updatedData = { ...data };
        
        setFormData(prevState => {
            const stepKey = step === 'stepFourData' ? 'stepFourData' : step;
            const newState = {
                ...prevState,
                [stepKey]: updatedData
            };
            //  console.log(`Updated form data for ${stepKey}:`, newState);
            return newState;
        });
    };

    const handleNext = () => {
        if (formData.concession_id === null || formData.concession_id === undefined) {
            // alert("La concession n'a pas pu être détectée. Veuillez re-sélectionner un vendeur.");
            toast.error("La concession n'a pas pu être détectée. Veuillez re-sélectionner un vendeur.");
            return;
          }
        setCurrentStep(prevStep => prevStep + 1);
    };

    const handlePrevious = () => {
        setCurrentStep(prevStep => prevStep - 1);
    };

    const handleComplete = () => {
        setCurrentStep(5);
    };

    const handleSave = async (step, data, {silent = false } = {}) => {
      try {
          if (formData.vendeur_id === null || formData.vendeur_id === undefined || formData.vendeur_id === '') {
            // alert("Veuillez sélectionner un vendeur avant d'enregistrer la fiche véhicule.");
            toast.error("Veuillez sélectionner un vendeur avant d'enregistrer la fiche véhicule.");
            return;
          }

          setSubmissionError(null);

          // Mettre à jour les données locales pour l'étape actuelle
          handleStepData(step, data);

          // Préparer les données pour l'API en préservant les données de location
          const updatedData = {
            etat: formData.etat,
            numero_dossier: formData.numero_dossier,
            statut: formData.statut,
            vendeur_id: formData.vendeur_id,
            concession_id: formData.concession_id,
            nom_client: formData.nom_client || '',
            telephone_client: formData.telephone_client || '',
            numero_chassis: formData.stepOneData?.veh_6 || '',
            ...formData.stepOneData,
            ...(step === 'stepOneData' ? data : {}),
            ...formData.stepTwoData,
            ...(step === 'stepTwoData' ? data : {}),
            ...formData.stepThreeData,
            ...(step === 'stepThreeData' ? data : {}),
            ...formData.stepFourData,
            ...(step === 'stepFourData' ? data : {}),
          };
          


          // Convertir les valeurs des boutons radio en nombres si nécessaire
          const radioFields = ['veh_33', 'veh_34', 'occ_veh_32', 'tit_1', 'tit_2', 'cotit_0', 'cotit_2',
            'occ_app_1', 'occ_app_2', 'occ_app_12', 'occ_tit_1', 'occ_tit_2',
            'loc_0', 'loc_1', 'loc_5', 'loc_17', 'loc_18', 'occ_cotit_0', 'occ_cotit_2',
            'veh_0', 'veh_5', 'ccv_1'];
          radioFields.forEach(field => {
            if (updatedData[field] !== undefined) {
              // Convertir en nombre si c'est une chaîne de caractères
              if (typeof updatedData[field] === 'string') {
                  updatedData[field] = parseInt(updatedData[field], 10);
              }
            }
          });

          // console.log("Données envoyées à l'API:", updatedData); //////////////////////////////////////////////////////////////////////// LOG

          if (!vehiculeId) {
              // Si la fiche n'existe pas encore, créer une nouvelle fiche
              const response = await axios.post(`${API_URL}/vehicules`, updatedData, {
                  headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                      "Content-Type": "application/json",
                  }
              });

                console.log('Vehicle created:', response.data.data.id);
              setVehiculeId(response.data.data.id); // Stocker l'ID de la fiche créée
          } else {
              // Si la fiche existe, mettre à jour la fiche existante
              await axios.put(`${API_URL}/vehicules/${vehiculeId}`, updatedData, {
                  headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                      "Content-Type": "application/json",
                  }
              });

                console.log('Vehicle updated successfully');
          }

          // alert("Données enregistrées avec succès !");
          if (!silent) {
            toast.success("Données enregistrées avec succès !");
          }
      } catch (error) {
          console.error("Erreur lors de l'enregistrement des données:", error);
          const errorMessage = error.response?.data?.error || "Une erreur s'est produite lors de l'enregistrement.";
          setSubmissionError(errorMessage);
          toast.error(`Erreur lors de l'enregistrement : ${errorMessage}`);
      }
    };

    useEffect(() => {
        if (vehiculeId !== null && currentStep === 4) {
             console.log("useEffect - Véhicule ID disponible:", vehiculeId);
            setCurrentStep(5);
        }
    }, [vehiculeId]);

    // Valide la sortie d'un step (mêmes règles que les boutons)
    const canLeaveStep = (fromStep) => {
    // Règle existante de handleNext : pour quitter l'étape 1 il faut une concession détectée
    if (fromStep === 1) {
        if (formData.concession_id === null || formData.concession_id === undefined) {
        alert("La concession n'a pas pu être détectée. Veuillez re-sélectionner un vendeur.");
        return false;
        }
    }
    // Tu peux ajouter d'autres validations si tu en as sur d'autres étapes
    return true;
    };

    // Click sur un step de la timeline
    const handleProgressClick = async (targetStep) => {
    if (targetStep === currentStep) return;
    if (targetStep < 1 || targetStep > 4) return; // la timeline affiche 1..4
    if (isNavigating) return;

    // Retour arrière : autorisé direct
    if (targetStep < currentStep) {
        setCurrentStep(targetStep);
        return;
    }

    try {
        setIsNavigating(true);
        // Avancer : on rejoue les validations step par step
        for (let s = currentStep; s < targetStep; s++) {
            if (!prerequisitesOkFor(s +1)) { return; }
            if (!canLeaveStep(s)) {
            return; // on bloque au premier step qui ne passe pas la validation
            }

            await saveStepByIndex(s, { silent: true })
            setCurrentStep(s +1);
        }
        setCurrentStep(targetStep);
        } finally {
            setIsNavigating(false);
        }

    }

    // Optionnel : savoir si un step est cliquable visuellement
    const canClickStep = (stepId) => {
    if (stepId < currentStep) return true;        // toujours possible de revenir
    if (stepId === currentStep) return false;     // inutile
    // Pour passer à l'étape 2 (ou plus), il faut satisfaire les validations de sortie des steps précédents.
    // Ici on ne “exécute” pas la boucle, on pré-évalue les conditions connues pour l'affichage (tooltip & style).
    if (stepId >= 2 && (formData.concession_id === null || formData.concession_id === undefined)) return false;
    return true;
    };

    // Optionnel : message d’aide au survol quand ce n’est pas cliquable
    const stepDisabledReason = (stepId) => {
    if (stepId >= 2 && (formData.concession_id === null || formData.concession_id === undefined)) {
        return "Sélectionnez un vendeur pour détecter la concession avant de continuer";
    }
    return "";
    };

    const getStepKeyByIndex = (index) => {
        switch (index) {
            case 1: return 'stepOneData';
            case 2: return 'stepTwoData';
            case 3: return 'stepThreeData';
            case 4: return 'stepFourData';
            default: return null;
        }
    };

    const saveStepByIndex = async (index, { silent = true } = {}) => {
        const stepKey = getStepKeyByIndex(index);
        if (!stepKey) return;
        const dataSlice = formData[stepKey] || {};
        await handleSave(stepKey, dataSlice, { silent });
    };


    const renderStepComponent = () => {
        if (currentStep === 1 && vehiculeType) {
            const stepOneReady =
                loading === false &&
                (Object.keys(formData.stepOneData || {}).length > 0 || !vehiculeId);

            if (!stepOneReady) return <div>Chargement du formulaire véhicule…</div>;

            const key = `step-one-${vehiculeId || 'new'}-${vehiculeType}`;
            if (vehiculeType === 'Neuf') {
                return (
                    <StepOneNeuf
                        key={key}
                        sendData={(data) => handleStepData('stepOneData', data)}
                        handleSave={(data) => handleSave('stepOneData', data)}
                        isActive={true}
                        onNext={handleNext}
                        commonData={formData}
                    />
                );
            } else {
                return (
                    <StepOneOccasion
                        key={key}
                        sendData={(data) => handleStepData('stepOneData', data)}
                        handleSave={(data) => handleSave('stepOneData', data)}
                        isActive={true}
                        onNext={handleNext}
                        commonData={formData}
                    />
                );
            }
        } else if (currentStep === 2) {
            return vehiculeType === 'Neuf' ? (
                <StepTwoNeuf
                    sendData={(data) => handleStepData('stepTwoData', data)}
                    handleSave={(data) => handleSave('stepTwoData', data)}
                    isActive={true}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    commonData={formData}
                />
            ) : (
                <StepTwoOccasion
                    sendData={(data) => handleStepData('stepTwoData', data)}
                    handleSave={(data) => handleSave('stepTwoData', data)}
                    isActive={true}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    commonData={formData}
                />
            );
        } else if (currentStep === 3) {
            return vehiculeType === 'Neuf' ? (
                <StepThreeNeuf
                    sendData={(data) => handleStepData('stepThreeData', data)}
                    handleSave={(data) => handleSave('stepThreeData', data)}
                    isActive={true}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    commonData={formData}
                />
            ) : (
                <StepThreeOccasion
                    sendData={(data) => handleStepData('stepThreeData', data)}
                    handleSave={(data) => handleSave('stepThreeData', data)}
                    isActive={true}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    commonData={formData}
                />
            );
        } else if (currentStep === 4) {
            return vehiculeType === 'Neuf' ? (
                <StepFourNeuf
                    sendData={(data) => handleStepData('stepFourData', data)}
                    handleSave={(data) => handleSave('stepFourData', data)}
                    isActive={true}
                    onPrevious={handlePrevious}
                    onComplete={handleComplete}
                    commonData={formData}
                    submissionError={submissionError}
                />
            ) : (
                <StepFourOccasion
                    sendData={(data) => handleStepData('stepFourData', data)}
                    handleSave={(data) => handleSave('stepFourData', data)}
                    isActive={true}
                    onPrevious={handlePrevious}
                    onComplete={handleComplete}
                    commonData={formData}
                    submissionError={submissionError}
                />
            );
        } else if (currentStep === 5) {
            return (
                <StepFiveCommon
                    commonData={formData}
                    vehiculeId={vehiculeId}
                    user_role={currentUser.statut}
                    onPrevious={handlePrevious}
                />
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className="loading-container">
                <p>Chargement des données...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
            </div>
        );
    }

    // Gestion de la navigation via la timeline
    const steps = [
        { id: 1, label: 'VÉHICULE'},
        { id: 2, label: 'TITULAIRES'},
        { id: 3, label: 'LOCATION'},
        { id: 4, label: 'SIGNATURES'},
    ]

    // conditions minimales pour avancer au step N (depuis n’importe où)
    const prerequisitesOkFor = (targetStep) => {
        if (targetStep <= 1) return true;
        // il faut au minimum savoir si VN/VO + vendeur sélectionné + concession détectée
        // if (!vehiculeType) { alert("Sélectionne d'abord le type de véhicule (Neuf/Occasion)."); return false; }
        if (!vehiculeType) { toast.error("Sélectionne d'abord le type de véhicule (Neuf/Occasion)."); return false; }
        // if (!formData.vendeur_id) { alert("Sélectionne d'abord un vendeur."); return false; }
        if (!formData.vendeur_id) { toast.error("Sélectionne d'abord un vendeur."); return false; }
        if (formData.concession_id === null || formData.concession_id === undefined) {
            // alert("La concession n'a pas pu être détectée. Re-sélectionne un vendeur.");
            toast.error("La concession n'a pas pu être détectée. Re-sélectionne un vendeur.");
            return false;
        }
        return true;
    };

    return (
        <div className="vehicle-form-container">
            <Header
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
                onMenuToggle={onMenuToggle}
            />
            <PageWrapper isMenuOpen={isMenuOpen}>
                <div className="step-container">
                    <div className="progress-indicator">
                    {steps.map(({ id, label }) => {
                        const isActive = currentStep === id;
                        const isClickable = canClickStep(id);
                        const classes = [
                        "progress-step",
                        isActive ? "active" : "",
                        isClickable ? "clickable" : "disabled"
                        ].join(" ").trim();

                        return (
                        <div
                            key={id}
                            className={classes}
                            onClick={() => isClickable && handleProgressClick(id)}
                            title={!isClickable ? stepDisabledReason(id) : ""}
                            role="button"
                            aria-current={isActive ? "step" : undefined}
                        >
                            <div className="step-content">{label}</div>
                        </div>
                        );
                    })}
                    </div>


                    {currentStep === 1 && (
                        <>
                            <div className="form-header">
                                <div className="form-group">
                                    <label htmlFor="numero_dossier">DOSSIER N°</label>
                                    <input
                                        type="text"
                                        id="numero_dossier"
                                        name="numero_dossier"
                                        value={formData.numero_dossier || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="statut">STATUT:</label>
                                    <select
                                        id="statut"
                                        name="statut"
                                        value={formData.statut || 'Commande'}
                                        onChange={handleInputChange}
                                    >
                                        <option value="Commande">Commande</option>
                                        <option value="Stock">Stock</option>
                                        <option value="Vendu">Vendu</option>
                                        <option value="Demo">Demo</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="vendeur_id">VENDEUR:</label>
                                    <select
                                        id="vendeur_id"
                                        name="vendeur_id"
                                        value={formData.vendeur_id || ''}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Sélectionner un vendeur</option>
                                        {vendeurs.map(vendeur => (
                                            <option key={vendeur.id} value={vendeur.id}>
                                                {vendeur.firstName} {vendeur.lastName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="section">
                                <h3>SÉLECTIONNER LE TYPE DE VÉHICULE</h3>
                                <div className="radio-group">
                                    <label>VÉHICULE:</label>
                                    <div className='radio-group-child'>
                                        <div>
                                            <input
                                                type="radio"
                                                id="neuf"
                                                name="vehiculeType"
                                                checked={vehiculeType === 'Neuf'}
                                                onChange={() => handleVehiculeTypeChange('Neuf')}
                                            />
                                            <label htmlFor="neuf">NEUF</label>
                                        </div>
                                        <div>
                                            <input
                                                type="radio"
                                                id="occasion"
                                                name="vehiculeType"
                                                checked={vehiculeType === 'Occasion'}
                                                onChange={() => handleVehiculeTypeChange('Occasion')}
                                            />
                                            <label htmlFor="occasion">OCCASION</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {renderStepComponent()}
                </div>
            </PageWrapper>
            <Footer />
        </div>
    );
};

VehiculeForm.propTypes = {
    menuState: PropTypes.shape({
        isMenuOpen: PropTypes.bool,
        setIsMenuOpen: PropTypes.func,
        onMenuToggle: PropTypes.func,
    }),
};

export default VehiculeForm;